"use client";

import { useCallback, useState } from "react";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { get_cart_query } from "@core/services/auth";
import {
	remove_all_from_cart_mutation,
	remove_from_cart_mutation,
} from "@core/services/products";
import { set_cookie } from "@core/helpers/cookie";
import { useLanguageContext } from "@core/helpers/client/contexts/LanguagesContext";

/** آیتم سبد را به شکل ورودی موتیشن‌های حذف تبدیل می‌کند. */
const to_remove_input = (item) => ({
	product_id: item?.product_id || undefined,
	bundle_id: item?.bundle_id || undefined,
	mix_variant: item?.mix_variant_keys || [],
	price_key: item?.price_key || null,
});

/**
 * خالی کردن کل سبد خرید.
 *
 * برای کاربر مهمان سبد در کوکی `local-cart` نگه داشته می‌شود، پس کوکی خالی و
 * سبد دوباره خوانده می‌شود. برای کاربر لاگین‌کرده موتیشن حذف کلی صدا زده
 * می‌شود و بعد نتیجه بازبینی می‌شود؛ هر آیتمی که سرور حذف نکرده باشد تک‌تک با
 * removeFromCart حذف می‌شود تا وضعیت UI و سرور از هم جدا نماند.
 */
const useClearCart = () => {
	const { cart, set_cart, is_auth } = useAuthContext();
	const lang = useLanguageContext()?.language || "fa";
	const [clearing, set_clearing] = useState(false);

	const clear_cart = useCallback(
		async (e) => {
			e?.preventDefault?.();

			const products = cart?.products || [];
			if (products.length === 0) return false;

			set_clearing(true);
			try {
				if (!is_auth) {
					await set_cookie("local-cart", JSON.stringify([]));
					const result = await get_cart_query({
						lang,
						local_cart: { products: [] },
					});
					if (result?.status) set_cart(result?.data);
					return !!result?.status;
				}

				let result = await remove_all_from_cart_mutation(
					{ lang, products: products.map(to_remove_input) },
				);

				// آیتم‌هایی که سرور هنوز حذفشان نکرده است.
				const remaining = result?.status
					? result?.data?.products || []
					: products;

				for (const item of remaining) {
					const single = await remove_from_cart_mutation({
						lang,
						...to_remove_input(item),
					});
					if (!single?.status) break;
					result = single;
				}

				if (result?.status) set_cart(result?.data);
				return !!result?.status;
			} finally {
				set_clearing(false);
			}
		},
		[cart, is_auth, set_cart, lang],
	);

	return { clear_cart, clearing };
};

export default useClearCart;
